import {
  type ProspectDto,
  type ProspectStage,
  type ProspectType,
  useSalesAddAssignment,
  useSalesCompleteTask,
  useSalesCreateContact,
  useSalesCreateInteraction,
  useSalesCreateNote,
  useSalesCreateTask,
  useSalesDeleteContact,
  useSalesDeleteInteraction,
  useSalesDeleteNote,
  useSalesDeleteTask,
  useSalesGetProspect,
  useSalesRemoveAssignment,
  useSalesUpdateContact,
  useSalesUpdateInteraction,
  useSalesUpdateNote,
  useSalesUpdateProspect,
  useSalesUpdateStage,
  useSalesUpdateTask,
} from "@crowdcoursing/api/openapi";
import { useCallback, useState } from "react";

import { useToast } from "~hooks/useToast";

type ProspectUpdateField =
  | "name"
  | "type"
  | "estimatedValue"
  | "prospectType"
  | "expectedCloseDate"
  | "region"
  | "source"
  | "website";

export interface ProspectDetailsOptimisticUpdates {
  name?: string;
  type?: string;
  stage?: ProspectStage;
  estimatedValue?: number;
  prospectType?: ProspectType;
  expectedCloseDate?: string;
  region?: string;
  source?: string;
  website?: string;
}

export const useProspectDetails = (prospectId: string) => {
  const toast = useToast();
  const [optimisticUpdates, setOptimisticUpdates] = useState<ProspectDetailsOptimisticUpdates>({});

  // Fetch prospect data
  const {
    data: prospectData,
    isLoading,
    error,
    refetch,
  } = useSalesGetProspect({
    pathParams: { id: prospectId },
  });

  // Mutation hooks
  const { mutateAsync: updateProspect } = useSalesUpdateProspect();
  const { mutateAsync: updateStage } = useSalesUpdateStage();
  const { mutateAsync: addAssignment } = useSalesAddAssignment();
  const { mutateAsync: removeAssignment } = useSalesRemoveAssignment();
  const { mutateAsync: createContact } = useSalesCreateContact();
  const { mutateAsync: updateContact } = useSalesUpdateContact();
  const { mutateAsync: removeContact } = useSalesDeleteContact();
  const { mutateAsync: createNote } = useSalesCreateNote();
  const { mutateAsync: updateNote } = useSalesUpdateNote();
  const { mutateAsync: removeNote } = useSalesDeleteNote();
  const { mutateAsync: createInteraction } = useSalesCreateInteraction();
  const { mutateAsync: updateInteraction } = useSalesUpdateInteraction();
  const { mutateAsync: removeInteraction } = useSalesDeleteInteraction();
  const { mutateAsync: addTask } = useSalesCreateTask();
  const { mutateAsync: updateTask } = useSalesUpdateTask();
  const { mutateAsync: deleteTask } = useSalesDeleteTask();
  const { mutateAsync: completeTask } = useSalesCompleteTask();

  // Combine actual data with optimistic updates
  const prospect: ProspectDto | null = prospectData
    ? {
        ...prospectData,
        ...optimisticUpdates,
      }
    : null;

  // Header field updates
  const updateField = useCallback(
    async (field: ProspectUpdateField, value: string | number) => {
      if (!prospect) {
        return;
      }

      // Apply optimistic update
      setOptimisticUpdates((prev) => ({ ...prev, [field]: value }));

      try {
        await updateProspect({
          body: { ...prospect, [field]: value },
          pathParams: { id: prospectId },
        });
      } catch (err) {
        // Revert optimistic update on error
        setOptimisticUpdates((prev) => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
        toast.error("Failed to update prospect");
      }
    },
    [prospect, prospectId, toast, updateProspect],
  );

  // Stage management
  const changeStage = useCallback(
    async (newStage: ProspectStage) => {
      if (!prospect) {
        return;
      }

      // Apply optimistic update
      setOptimisticUpdates((prev) => ({ ...prev, stage: newStage }));

      try {
        await updateStage({
          body: { stage: newStage },
          pathParams: { id: prospectId },
        });
      } catch (err) {
        // Revert optimistic update on error
        setOptimisticUpdates((prev) => {
          const { stage: _, ...rest } = prev;
          return rest;
        });
        toast.error("Failed to update stage");
      }
    },
    [prospect, prospectId, toast, updateStage],
  );

  // Assignment management
  const assignUser = useCallback(
    async (userId: string) => {
      try {
        // First add the assignment
        await addAssignment({
          body: { userId },
          pathParams: { id: prospectId },
        });

        // Then update the prospect to set the owner
        if (prospect) {
          await updateProspect({
            body: {
              ...prospect,
              ownerUserId: userId,
            },
            pathParams: { id: prospectId },
          });
        }

        await refetch();
      } catch (err) {
        toast.error("Failed to assign user");
      }
    },
    [prospectId, refetch, toast, addAssignment, updateProspect, prospect],
  );

  const unassignUser = useCallback(
    async (userId: string) => {
      try {
        await removeAssignment({
          pathParams: { id: prospectId, userId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to unassign user");
      }
    },
    [prospectId, refetch, toast, removeAssignment],
  );

  // Contact management
  const addNewContact = useCallback(
    async (contactData: Record<string, unknown>) => {
      try {
        await createContact({
          body: contactData,
          pathParams: { id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to add contact");
      }
    },
    [prospectId, refetch, toast, createContact],
  );

  const updateExistingContact = useCallback(
    async (contactId: string, contactData: Record<string, unknown>) => {
      try {
        await updateContact({
          body: contactData,
          pathParams: { contactId, id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to update contact");
      }
    },
    [prospectId, refetch, toast, updateContact],
  );

  const deleteContact = useCallback(
    async (contactId: string) => {
      try {
        await removeContact({
          pathParams: { contactId, id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to delete contact");
      }
    },
    [prospectId, refetch, toast, removeContact],
  );

  // Note management
  const addNewNote = useCallback(
    async (noteData: Record<string, unknown>) => {
      try {
        await createNote({
          body: noteData,
          pathParams: { id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to add note");
      }
    },
    [prospectId, refetch, toast, createNote],
  );

  const updateExistingNote = useCallback(
    async (noteId: string, noteData: Record<string, unknown>) => {
      try {
        await updateNote({
          body: noteData,
          pathParams: { id: prospectId, noteId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to update note");
      }
    },
    [prospectId, refetch, toast, updateNote],
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      try {
        await removeNote({
          pathParams: { id: prospectId, noteId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to delete note");
      }
    },
    [prospectId, refetch, toast, removeNote],
  );

  // Interaction management
  const addNewInteraction = useCallback(
    async (interactionData: Record<string, unknown>) => {
      try {
        await createInteraction({
          body: interactionData,
          pathParams: { id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to add interaction");
      }
    },
    [prospectId, refetch, toast, createInteraction],
  );

  const updateExistingInteraction = useCallback(
    async (interactionId: string, interactionData: Record<string, unknown>) => {
      try {
        await updateInteraction({
          body: interactionData,
          pathParams: { id: prospectId, interactionId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to update interaction");
      }
    },
    [prospectId, refetch, toast, updateInteraction],
  );

  const deleteInteraction = useCallback(
    async (interactionId: string) => {
      try {
        await removeInteraction({
          pathParams: { id: prospectId, interactionId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to delete interaction");
      }
    },
    [prospectId, refetch, toast, removeInteraction],
  );

  // Task management
  const addNewTask = useCallback(
    async (taskData: Record<string, unknown>) => {
      try {
        await addTask({
          body: taskData,
          pathParams: { id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to add task");
      }
    },
    [prospectId, refetch, toast, addTask],
  );

  const updateExistingTask = useCallback(
    async (taskId: string, taskData: Record<string, unknown>) => {
      try {
        await updateTask({
          body: taskData,
          pathParams: { taskId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to update task");
      }
    },
    [prospectId, refetch, toast, updateTask],
  );

  const removeTask = useCallback(
    async (taskId: string) => {
      try {
        await deleteTask({
          pathParams: { taskId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to delete task");
      }
    },
    [refetch, toast, deleteTask],
  );

  const completeExistingTask = useCallback(
    async (taskId: string) => {
      try {
        await completeTask({
          pathParams: { taskId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to complete task");
      }
    },
    [refetch, toast, completeTask],
  );

  return {
    // Contact actions
    addNewContact,

    // Interaction actions
    addNewInteraction,

    // Note actions
    addNewNote,

    // Task actions
    addNewTask,

    // Assignment actions
    assignUser,

    changeStage,

    completeExistingTask,

    deleteContact,

    deleteInteraction,

    deleteNote,

    error,

    isLoading,

    prospect,

    removeTask,

    unassignUser,

    updateExistingContact,

    updateExistingInteraction,

    updateExistingNote,

    updateExistingTask,
    // Header actions
    updateField: updateField as (
      field:
        | "name"
        | "type"
        | "estimatedValue"
        | "prospectType"
        | "expectedCloseDate"
        | "region"
        | "source"
        | "website",
      value: string | number,
    ) => Promise<void>,
  };
};
